import { HTTPMethod, type User } from "@fusionauth/typescript-client";
import { Settings } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIconOutlined from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LoginIcon from "@mui/icons-material/Login";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Link as MuiLink,
  Select,
  Snackbar,
  type SnackbarCloseReason,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import {
  GridActionsCellItem,
  type GridColDef,
  type GridRenderCellParams,
  Toolbar,
} from "@mui/x-data-grid-premium";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import CustomDialog from "../../../../components/CustomDialog";
import BaseTable from "../../../../components/datagrid/BaseTable.tsx";
import DatagridQuickFilter from "../../../../components/datagrid/DatagridQuickFilter.tsx";
import { TableNoRowsCustomization } from "../../../../components/datagrid/TableNoRowsCustomization";
import { TableToolbarButton } from "../../../../components/datagrid/TableToolbarButton";
import { useFetch } from "../../../../services/hooks/useFetch";
import theme from "../../../../styles/theme";
import { apiPrefix, userApiPrefix } from "../../../../utils/main";

const ROLES = ["USER", "CLIENT_ADMIN", "WORKSHOP", "SUPPORT", "ADMIN"];

type SelectChangeEvent =
  | React.ChangeEvent<HTMLInputElement>
  | (Event & {
      target: {
        value: string[];
        name: string;
      };
    });

const CustomNoProjectRowsOverlay = () => {
  return (
    <TableNoRowsCustomization
      icon={
        <LockOutlinedIcon
          style={{
            color: "#0D3E56",
            width: "32px",
            height: "32px",
            marginLeft: "18px",
          }}
        />
      }
      text={"This user cannot access any projects yet."}
      subtext={"Once access has been provided the projects will be shown here."}
      iconBackground={48}
    />
  );
};

const CustomNoCompanyProjectRowsOverlay = () => {
  return (
    <TableNoRowsCustomization
      icon={
        <Settings
          style={{
            color: "#0D3E56",
            width: "32px",
            height: "32px",
            marginLeft: "18px",
          }}
        />
      }
      text={"There are no active projects for this user's company."}
      subtext={"Once a project has been created, it will be shown here."}
      iconBackground={48}
    />
  );
};

const signUpEmailRegex = new RegExp(
  /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-z0-9])?\.)+[a-zA-Z]{2,}$/,
);

export default function UserDetails({
  user,
  companies,
  isEdit,
  triggerUserFetch,
}: {
  user: User | undefined;
  companies: Company[];
  isEdit: boolean;
  triggerUserFetch: () => void;
}) {
  function CustomToolBar(isManagementModal: boolean) {
    return () => (
      <Toolbar>
        <DatagridQuickFilter searchColumns={""} />
        <Box sx={{ flexGrow: 1 }} />
        {isManagementModal ? (
          <TableToolbarButton
            startIcon={<CloseIcon />}
            onClick={() => setShowProjectAccessModal(false)}
          />
        ) : (
          <TableToolbarButton
            startIcon={<Settings />}
            onClick={handleOpenProjectManagement}
          >
            Manage Project Access
          </TableToolbarButton>
        )}
      </Toolbar>
    );
  }

  const { update: saveUser } = useFetch<void, SaveUserRequest>();
  const {
    data: userProjects,
    fetch: fetchUserProjects,
    reset: resetUserProjects,
    isLoading: userProjectsLoading,
  } = useFetch<UserProject[], Partial<UserProject>>();
  const { delete: deleteUserProjects, create: createUserProject } = useFetch<
    undefined,
    Partial<UserProject>
  >();
  const {
    data: companyProjects,
    fetch: fetchCompanyProjects,
    isLoading: companyProjectsLoading,
  } = useFetch<UserProject[], undefined, Project[]>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [displayErrors, setDisplayErrors] = useState(false);
  const [showProjectAccessModal, setShowProjectAccessModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [updateDetails, setUpdateDetails] = useState<string>("");
  const [confirmationRow, setConfirmationRow] = useState<
    UserProject | undefined
  >(undefined);
  const [allCompanyProjectRows, setAllCompanyProjectRows] = useState<
    UserProject[] | undefined
  >(undefined);

  const [emailMessage, setEmailMessage] = useState("");

  const companyIds =
    user && user.memberships
      ? user.memberships.map((m) => m.groupId)
      : undefined;

  const [currentCompanies, setCurrentCompanies] = useState(
    companyIds ? companies?.filter((c) => companyIds.includes(c.id!)) : [],
  );
  const [companyIdForProjects, setCompanyIdForProjects] = useState<
    string | undefined
  >(currentCompanies[0]?.id || undefined);

  const appRegistration = useMemo(() => {
    const clientId =
      import.meta.env.VITE_AUTH_CLIENT_ID ||
      (window._env_ && window._env_.AUTH_CLIENT_ID);

    return user && user.registrations
      ? user.registrations.find((r) => r.applicationId === clientId)
      : null;
  }, [user]);

  const [roles, setRoles] = useState(
    appRegistration && appRegistration.roles ? appRegistration.roles : [],
  );
  const [email, setEmail] = useState(user?.email || "");
  const [fullName, setFullName] = useState(user?.fullName || "");

  const handleRoleChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event;
    setRoles(
      // On autofill, we get a stringified value.
      typeof value === "string" ? value.split(",") : value,
    );
  };

  const handleSubmit = () => {
    let errors = false;
    if (email.length > 0) {
      if (!signUpEmailRegex.test(email)) {
        errors = true;
        setEmailMessage("E-mail not in valid format");
      } else {
        setEmailMessage("");
      }
    }
    if (
      !currentCompanies ||
      currentCompanies.length === 0 ||
      roles.length === 0 ||
      email === "" ||
      fullName === "" ||
      updateDetails === ""
    ) {
      errors = true;
    }
    setDisplayErrors(errors);
    if (!errors) {
      const updatedUser: Partial<User> = { ...user };
      delete updatedUser.identities; // don't want to send identities in this request since it's not needed and causes issues with the FA model
      updatedUser.email = email;
      updatedUser.fullName = fullName;
      updatedUser.firstName = fullName.split(" ")[0]; // use the first chunk of the full name as first name
      // save user changes
      void saveUser({
        path: `${userApiPrefix}/user/`,
        body: {
          user: updatedUser,
          companyIds: currentCompanies?.map((c) => c.id!) || [],
          roles: roles,
          comment: updateDetails,
        },
        method: HTTPMethod.POST,
        postResponseAction: () => {
          handleEdit(false);
          triggerUserFetch();
        },
      });
    }
  };

  const handleEdit = (setEdit: boolean) => {
    searchParams.set("mode", setEdit ? "modify" : "detail");
    setSearchParams(searchParams);
    setUpdateDetails("");
    setDisplayErrors(false);
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    // this timeout prevents the weird flash of the dynamic modal contents
    // disappearing before the dialog actually transitions out
    setTimeout(() => {
      setConfirmationRow(undefined);
    }, theme.transitions.duration.leavingScreen);
  };

  const handleSnackbarClose = (
    _event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setShowSnackbar(false);
    setConfirmationRow(undefined);
  };

  const handleRevokeAccess = (userProject: UserProject) => {
    void deleteUserProjects({
      path: `${apiPrefix}/user-project/${userProject.id}`,
      postResponseAction: () => {
        // refetch user projects
        void fetchUserProjects(
          `${apiPrefix}/user-project/${user?.id}/${companyIdForProjects}`,
        );
        setShowSnackbar(true);
        setShowConfirmation(false);
        triggerUserFetch();
      },
    });
  };

  const handleProvideAccess = (userProject: UserProject) => {
    void createUserProject({
      path: `${apiPrefix}/user-project/`,
      body: userProject,
      postResponseAction: () => {
        // refetch user projects
        void fetchUserProjects(
          `${apiPrefix}/user-project/${user?.id}/${companyIdForProjects}`,
        );
        setShowSnackbar(true);
        setShowConfirmation(false);
        triggerUserFetch();
      },
    });
  };

  const handleOpenProjectManagement = useCallback(() => {
    void fetchCompanyProjects(
      `${apiPrefix}/projects/company/${companyIdForProjects}`,
      {
        responseManipulationFunction: (data: Project[]) => {
          return data.map((p) => {
            return {
              userId: user!.id,
              project: p,
            };
          });
        },
        postResponseAction: () => {
          setShowProjectAccessModal(true);
        },
      },
    );
  }, [fetchCompanyProjects, companyIdForProjects, user]);

  const userProjectTableFields: GridColDef[] = [
    {
      field: "project.name",
      valueGetter: (_value, row) => {
        return row.project.name;
      },
      headerName: "Project Name",
      minWidth: 250,
      flex: 1,
    },
    {
      field: "project.status",
      valueGetter: (_value, row) => {
        return row.project.statusName;
      },
      renderCell: (params: GridRenderCellParams) => {
        const project = params.row.project;
        let statusColor: "default" | "primary" | "success" | "secondary" =
          "default";
        switch (project.status) {
          case 0:
            statusColor = "primary";
            break;
          case 1:
            statusColor = "secondary";
            break;
          default:
            statusColor = "success";
            break;
        }
        return project.isActive ? (
          <Chip label={project.statusName} color={statusColor} size="small" />
        ) : (
          <Chip label={"Inactive"} color={"error"} size="small" />
        );
      },
      headerName: "Status",
      width: 150,
    },
    {
      field: "project.patientCount",
      valueGetter: (_value, row) => {
        return row.project.patientCount;
      },
      headerName: "Patient Records",
      width: 150,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 150,
      align: "center",
      getActions: ({ row }) => {
        return [
          // todo: placeholder for future ticket to enhance user management UI
          // <GridActionsCellItem
          //   key={row.id}
          //   icon={<Lock fontSize="small" />}
          //   label="Lock User"
          //   className="textPrimary"
          //   onClick={() => {
          //     /* empty */
          //   }}
          //   color="inherit"
          // />,
          <GridActionsCellItem
            key={row.id}
            icon={<DeleteIconOutlined fontSize="small" />}
            title="Revoke Access"
            label="Revoke Access"
            className="textPrimary"
            onClick={() => {
              setShowConfirmation(true);
              setConfirmationRow(row);
            }}
            color="inherit"
          />,
        ];
      },
    },
  ];

  const allProjectsTableFields: GridColDef[] = userProjectTableFields
    .filter((col) => col.field !== "actions")
    .concat({
      field: "id",
      headerName: "Project Access",
      width: 200,
      align: "center",
      renderCell: ({ row }) => {
        return row.id ? (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIconOutlined fontSize="small" />}
            onClick={() => {
              setShowConfirmation(true);
              setShowSnackbar(false);
              setConfirmationRow(row);
            }}
          >
            Revoke Access
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<LoginIcon fontSize="small" />}
            onClick={() => {
              setShowConfirmation(true);
              setShowSnackbar(false);
              setConfirmationRow(row);
            }}
          >
            Provide Access
          </Button>
        );
      },
    });

  useEffect(() => {
    if (user && !userProjectsLoading && !userProjects) {
      void fetchUserProjects(
        `${apiPrefix}/user-project/${user.id}/${companyIdForProjects}`,
      );
    }
  }, [
    fetchUserProjects,
    companyIdForProjects,
    user,
    userProjects,
    userProjectsLoading,
  ]);

  useEffect(() => {
    // merge with userProject rows to get full list for management modal
    if (!companyProjectsLoading && companyProjects) {
      let rows = companyProjects;
      if (userProjects) {
        rows = userProjects.concat(
          rows.filter(
            (r) => !userProjects.some((up) => up.project.id === r.project.id),
          ),
        );
      }
      setAllCompanyProjectRows(rows);
    }
  }, [companyProjects, companyProjectsLoading, userProjects]);

  return (
    <div
      style={{
        height: "100%",
        minHeight: "800px",
        width: "calc(90vw - 15px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid>
        <Typography variant="h4" fontWeight="bold" textAlign="left">
          {user ? user.fullName : "New User"}
        </Typography>
      </Grid>
      <br />
      <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
        <Grid size={{ xs: 4, sm: 4, md: 6 }}>
          {isEdit ? (
            <TextField
              required
              fullWidth
              variant="outlined"
              label="Email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              error={(displayErrors && email === "") || emailMessage.length > 0}
              helperText={
                emailMessage.length > 0
                  ? emailMessage
                  : displayErrors && email === ""
                    ? "This field is required"
                    : ""
              }
            />
          ) : (
            <>
              <Typography variant="caption">Email *</Typography>
              <Typography variant="body2" sx={{ width: "50ch" }}>
                {email}
              </Typography>
            </>
          )}
        </Grid>
        <Grid size={{ xs: 4, sm: 4, md: 6 }}>
          {isEdit ? (
            <TextField
              required
              fullWidth
              variant="outlined"
              label="Full Name"
              value={fullName}
              onChange={(event) => {
                setFullName(event.target.value);
              }}
              error={displayErrors && fullName === ""}
              helperText={
                displayErrors && fullName === "" ? "This field is required" : ""
              }
            />
          ) : (
            <>
              <Typography variant="caption">Full Name *</Typography>
              <Typography variant="body2" sx={{ width: "50ch" }}>
                {fullName}
              </Typography>
            </>
          )}
        </Grid>
        <Grid size={{ xs: 4, sm: 8, md: 12 }}>
          {isEdit ? (
            <FormControl fullWidth error={displayErrors && roles.length === 0}>
              <InputLabel id="roles-label" required>
                Roles
              </InputLabel>
              <Select
                labelId="roles-label"
                label="Roles"
                required
                multiple
                variant="outlined"
                value={roles}
                onChange={handleRoleChange}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value: string) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
              >
                {ROLES.map((name) => (
                  <MenuItem key={name} value={name}>
                    <Checkbox checked={roles.includes(name)} />
                    <ListItemText primary={name} />
                  </MenuItem>
                ))}
              </Select>

              <FormHelperText>
                {displayErrors && roles.length === 0
                  ? "This field is required"
                  : ""}
              </FormHelperText>
            </FormControl>
          ) : (
            <>
              <Typography variant="caption">Roles *</Typography>
              <Typography variant="body2" sx={{ width: "50ch" }}>
                {roles.join(", ")}
              </Typography>
            </>
          )}
        </Grid>
        <Grid size={{ xs: 4, sm: 4, md: 6 }}>
          {isEdit ? (
            <Autocomplete
              disablePortal
              options={companies ? companies : []}
              getOptionLabel={(option) => {
                if (option.name) {
                  return option.name;
                } else {
                  return "";
                }
              }}
              renderInput={(params) => (
                <TextField
                  required
                  error={displayErrors && currentCompanies?.length === 0}
                  helperText={
                    displayErrors && currentCompanies?.length === 0
                      ? "This field is required"
                      : ""
                  }
                  {...params}
                  label="Company"
                />
              )}
              value={currentCompanies}
              onChange={(_event, value) => {
                setCurrentCompanies(value);
              }}
              isOptionEqualToValue={(option, value) => {
                if (value === null) {
                  return true;
                }
                return option.id && value.id ? option.id === value.id : false;
              }}
              multiple
            />
          ) : (
            <>
              <Typography variant="caption">Company *</Typography>
              <Typography variant="body2" sx={{ width: "50ch" }}>
                {currentCompanies.map((c) => c.name).join(", ")}
              </Typography>
            </>
          )}
        </Grid>
        {isEdit && (
          <Grid size={{ xs: 4, sm: 4, md: 6 }}>
            <TextField
              required
              label="Ticket Number/Reason for Updating the User"
              multiline
              fullWidth
              value={updateDetails}
              error={displayErrors && updateDetails === ""}
              helperText={
                displayErrors && updateDetails === ""
                  ? "This field is required"
                  : ""
              }
              onChange={(event) => {
                setUpdateDetails(event.target.value);
              }}
            ></TextField>
          </Grid>
        )}
      </Grid>
      <br />
      <Stack direction="row" spacing={1}>
        <MuiLink
          component={Link}
          to={`/settings?settings=user`}
          variant="body2"
        >
          Back to List
        </MuiLink>
        <Box flexGrow={1} />
        {isEdit ? (
          <Button
            startIcon={<SaveIcon />}
            size="small"
            variant="contained"
            onClick={handleSubmit}
          >
            Save User
          </Button>
        ) : (
          <Button
            startIcon={<EditIcon />}
            size="small"
            variant="contained"
            onClick={() => handleEdit(true)}
          >
            Edit User
          </Button>
        )}
      </Stack>
      {user?.id && !roles.includes("ADMIN") && (
        <>
          <br />
          <Grid>
            <Typography variant="h6" textAlign="left" paddingBottom={1}>
              {user ? user.fullName : "New User"}'s Projects for{" "}
              <Select
                variant="outlined"
                size="small"
                value={companyIdForProjects}
                onChange={(event) => {
                  resetUserProjects();
                  setCompanyIdForProjects(event.target.value);
                }}
                renderValue={(selected) =>
                  companies?.find((c) => c.id === selected)?.name
                }
              >
                {currentCompanies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </Typography>
            <Alert severity="info">
              The projects listed here are for the current environment only.
            </Alert>
          </Grid>
          <br />
          <Grid minHeight={350} maxHeight={1}>
            <BaseTable<UserProject>
              tableHeight={350}
              disableColumnFilter
              slots={{
                toolbar: CustomToolBar(false),
                noRowsOverlay: CustomNoProjectRowsOverlay,
              }}
              columns={userProjectTableFields}
              loading={userProjectsLoading}
              rows={userProjects || []}
              tableId="user-assigned-projects-table"
              sx={{
                "& .MuiDataGrid-cell": {
                  alignContent: "center",
                },
              }}
            />
          </Grid>
        </>
      )}
      <Dialog
        open={showProjectAccessModal}
        onClose={() => {
          setShowProjectAccessModal(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
      >
        <DialogContent
          sx={{
            height: "500px",
          }}
        >
          <BaseTable<UserProject>
            tableHeight={450}
            disableColumnFilter
            slots={{
              toolbar: CustomToolBar(true),
              noRowsOverlay: CustomNoCompanyProjectRowsOverlay,
            }}
            columns={allProjectsTableFields}
            loading={companyProjectsLoading || userProjectsLoading}
            rows={allCompanyProjectRows || []}
            sx={{
              "& .MuiDataGrid-cell": {
                alignContent: "center",
              },
            }}
            getRowId={(row) =>
              `${row.id ?? crypto.randomUUID()}-${row.project.id ?? crypto.randomUUID()}`
            }
            tableId="all-company-projects-table"
          />
        </DialogContent>
      </Dialog>
      <CustomDialog
        open={showConfirmation}
        handleClose={handleCloseConfirmation}
        hasConfirmation
        handleConfirm={() => {
          if (confirmationRow) {
            if (confirmationRow.id) {
              handleRevokeAccess(confirmationRow);
            } else {
              handleProvideAccess(confirmationRow);
            }
          }
        }}
        confirmationText={`${confirmationRow?.id ? "Revoke" : "Provide"} Access`}
      >
        <div
          style={{
            width: "350px",
          }}
        >
          <Typography variant="body1">
            {confirmationRow?.id ? "Revoke" : "Provide"} access to the{" "}
            {confirmationRow?.project.name} project for {user?.fullName}?
          </Typography>
        </div>
      </CustomDialog>
      <Snackbar
        open={showSnackbar}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Access {confirmationRow?.id ? "revoked" : "provided"} to{" "}
          {confirmationRow?.project.name}.
        </Alert>
      </Snackbar>
    </div>
  );
}
